import React from 'react';
import {
  AdminLegendVariant,
  convertToDecimals,
  currency,
  fallbackText,
  perHour,
  PharmacistLegendVariant,
  PharmacyLegendVariant,
  ReplacementLabelsStyled,
  ServiceTypes,
  TypeOfUser,
  WorkshiftReportStatus,
  WorkshiftStatus,
} from '@pharmaplan/common';
import type {
  IAdminBookedProfessional,
  IAdminPharmacyWorkshiftsListData,
  ICalendarEventType,
  TimeFormat,
} from '@pharmaplan/common';
import ServiceHandler, {
  idStore,
} from '@pharmaplan/common/helpers/ServiceHandler';
import { goToFallback } from '@pharmaplan/common/helpers/utils';
import strings from '../../../../localization';
import { getDayMonthDateYear } from '../../../Reports/helpers';
import { timeString } from '../../../../helpers/Functions';
import {
  DynamicTableCell,
  OtherScreens,
  PharmacyWorkshiftStatusLabels,
  ReplacementLabels,
} from '../../../../helpers/Constants';
import { handleValue } from '../../Profile/Pharmacy/PharmacyViewProfile/helper';
import { IUserProfileLinkSignature } from '../../../../hooks/Admin/useUserProfileLink';
import deleteRequestIcon from '../../../../assets/svg/delete.svg';
import requestCancellationIcon from '../../../../assets/svg/requestCancellation.svg';
import editWorkshift from '../../../../assets/svg/edit.svg';
import findMatchingIcon from '../../../../assets/svg/findMatching.svg';
import expressBookingIcon from '../../../../assets/svg/expressBooking.svg';
import { dateRecord, rateRecord } from '../../../../helpers/longTermRecord';
import { IShowCancellationParams } from '../../../../hooks/Admin/useWorkshiftDelete';
import ThemeConstants from '../../../../theme/ThemeConstants';
import LimitedTooltipList from '../../../common/LimitedTooltipList';
import type { ILimitedTooltipListItem } from '../../../common/LimitedTooltipList';

const { text, customLink, checkbox, chip } = DynamicTableCell;

const isSelfService = ServiceTypes.self === ServiceHandler.getService();

interface IListOfWorkshiftHeader {
  key: string;
  label: string;
  sortingKey?: string;
  isCenterAligned?: boolean;
}

interface IPharmacyWorkshiftListRow {
  data: IAdminPharmacyWorkshiftsListData[];
  timeFormat: TimeFormat;
  goToUserProfile: IUserProfileLinkSignature;
  showWorkshiftDelete: (
    workshiftId: string,
    dateTime: string,
    longTermShiftContractId?: string,
  ) => void;
  showRequestCancellation: ({
    bookingId,
    dateTime,
    longTermShiftContractId,
    professionalId,
  }: IShowCancellationParams & {
    bookedProfessionals: Array<IAdminBookedProfessional>;
  }) => void;
  findMatchingAvailabilities: (workshiftId: string) => void;
  showExpressBooking: (workshiftId: string) => void;
  canDelete: boolean;
  canEditBookings: boolean;
  showEditBookings: (
    longTermShiftContractId: string,
    workshiftId: string,
    bookedProfessionals: IAdminBookedProfessional[],
  ) => void;
  isPast: boolean;
  showRequestedProfessionals: ({
    workshiftId,
    professionalId,
    longTermShiftContractId,
  }: {
    workshiftId: string;
    professionalId?: string;
    longTermShiftContractId?: string;
  }) => void;
  handleEdit: (
    type: ICalendarEventType,
    bookingId: string | null,
    workshiftId: string,
  ) => () => void;
  selectedItems?: Array<string>;
  handlePress: (id: string, count?: number) => () => void;
  disableActions?: boolean;
}

const statusStyle = {
  [WorkshiftReportStatus.booked]: {
    color: ThemeConstants.admin.booked,
    backgroundColor: ThemeConstants.admin.requestedLight,
  },
  [WorkshiftReportStatus.posted]: {
    color: ThemeConstants.admin.posted,
    backgroundColor: ThemeConstants.admin.postedLight,
  },
  [WorkshiftReportStatus.request]: {
    color: ThemeConstants.admin.requested,
    backgroundColor: ThemeConstants.admin.requestedLight,
  },
  [WorkshiftReportStatus.partiallyBooked]: {
    color: ThemeConstants.admin.booked,
    backgroundColor: ThemeConstants.admin.bookedLight,
  },
  [WorkshiftReportStatus.partiallyRequested]: {
    color: ThemeConstants.admin.requested,
    backgroundColor: ThemeConstants.admin.requestedLight,
  },
  [WorkshiftReportStatus.partiallyRequestedBooked]: {
    color: ThemeConstants.admin.requested,
    backgroundColor: ThemeConstants.admin.requestedLight,
  },
  [AdminLegendVariant.booked]: {
    color: ThemeConstants.admin.booked,
    backgroundColor: ThemeConstants.admin.bookedLight,
  },
  [AdminLegendVariant.posted]: {
    color: ThemeConstants.admin.posted,
    backgroundColor: ThemeConstants.admin.postedLight,
  },
  [AdminLegendVariant.requested]: {
    color: ThemeConstants.admin.requested,
    backgroundColor: ThemeConstants.admin.requestedLight,
  },
};

const statusTypeMap = {
  [WorkshiftReportStatus.booked]: AdminLegendVariant.booked,
  [WorkshiftReportStatus.posted]: AdminLegendVariant.posted,
  [WorkshiftReportStatus.request]: AdminLegendVariant.requested,
  [WorkshiftReportStatus.partiallyBooked]: AdminLegendVariant.posted,
  [WorkshiftReportStatus.partiallyRequested]: AdminLegendVariant.posted,
  [WorkshiftReportStatus.partiallyRequestedBooked]: AdminLegendVariant.posted,
};

const hasRate = (rate?: number | null): rate is number =>
  rate !== undefined && rate !== null;

const formatHourlyRate = (rate?: number | null, separator = ' ') =>
  (hasRate(rate)
    ? `${currency}${convertToDecimals(rate)}${separator}${perHour}`
    : fallbackText);

const formatWeekdayWeekendRates = (
  weekdayRate?: number | null,
  weekendRate?: number | null,
) =>
  [
    formatHourlyRate(weekdayRate, ''),
    formatHourlyRate(weekendRate, ''),
  ].join(', ');

const getProfessionalHourlyRate = (
  professional: IAdminBookedProfessional,
) => {
  const {
    weekDayPaidHourlyRate,
    weekendPaidHourlyRate,
  } = professional ?? {};

  if (!hasRate(weekDayPaidHourlyRate) && !hasRate(weekendPaidHourlyRate)) {
    return null;
  }

  return formatWeekdayWeekendRates(weekDayPaidHourlyRate, weekendPaidHourlyRate);
};

const getPharmacyHourlyRate = (professional: IAdminBookedProfessional) => {
  const {
    pharmacyWeekDayHourlyRate,
    pharmacyWeekendHourlyRate,
  } = professional ?? {};

  if (
    !hasRate(pharmacyWeekDayHourlyRate)
    && !hasRate(pharmacyWeekendHourlyRate)
  ) {
    return null;
  }

  return formatWeekdayWeekendRates(
    pharmacyWeekDayHourlyRate,
    pharmacyWeekendHourlyRate,
  );
};

const getBookedProfessionalRateItems = ({
  bookedProfessionals,
  getRate,
}: {
  bookedProfessionals: Array<IAdminBookedProfessional>;
  getRate: (professional: IAdminBookedProfessional) => string | null;
}) =>
  bookedProfessionals.reduce<Array<ILimitedTooltipListItem>>(
    (acc, professional, index) => {
      const rate = getRate(professional);

      if (!rate) {
        return acc;
      }

      acc.push({
        key: `${professional.professionalId}-${index}-${rate}`,
        label: rate,
      });

      return acc;
    },
    [],
  );

export const pharmacyWorkshiftsListHeaders = () =>
  [
    {
      key: '0',
      label: `${strings.date} | ${strings.time}`,
      sortingKey: 'startDate',
    },
    { key: '1', label: strings.status },
    {
      key: '2',
      label: strings.totalHours,
      isCenterAligned: true,
      sortingKey: 'totalHours',
    },
    { key: '3', label: strings.replacementName },
    {
      key: '3.5',
      label: strings.replacementType,
      sortingKey: 'replacementType',
    },
    {
      key: '5',
      label: isSelfService
        ? strings.hourlyRate
        : strings.formatString(strings.hourlyRateFormat, strings.professional),
      isCenterAligned: true,
      sortingKey: 'paidHourlyRate',
    },
    !isSelfService && {
      key: '4',
      label: strings.pharmacyHourlyRate,
      isCenterAligned: true,
      sortingKey: 'pharmacyHourlyRate',
    },
    { key: '6', label: strings.action },
  ].filter(Boolean) as Array<IListOfWorkshiftHeader>;

export const pastWorkshiftsListHeaders = () =>
  [
    {
      key: '0',
      label: `${strings.date} | ${strings.time}`,
      sortingKey: 'startDate',
    },
    { key: '1', label: strings.status, sortingKey: 'status' },
    {
      key: '2',
      label: strings.totalHours,
      isCenterAligned: true,
      sortingKey: 'totalHours',
    },
    { key: '5', label: strings.replacementName, sortingKey: 'pharmacistName' },
    {
      key: '6',
      label: strings.replacementType,
      sortingKey: 'replacementType',
    },
    {
      key: '4',
      label: strings.formatString(
        strings.hourlyRateFormat,
        strings.professional,
      ),
      isCenterAligned: true,
      sortingKey: 'paidHourlyRate',
    },
    !isSelfService && {
      key: '3',
      label: strings.pharmacyHourlyRate,
      isCenterAligned: true,
      sortingKey: 'pharmacyHourlyRate',
    },
  ].filter(Boolean) as Array<IListOfWorkshiftHeader>;

export const statusMap = {
  [WorkshiftStatus.Posted]: strings.posted,
  [WorkshiftStatus.Requested]: strings.requested,
  [WorkshiftStatus.Booked]: strings.booked,
  [WorkshiftStatus.Unfulfilled]: strings.unfulfilled,
};

export const pharmacyWorkshiftsListRow = ({
  data,
  timeFormat,
  goToUserProfile,
  showWorkshiftDelete,
  showRequestCancellation,
  findMatchingAvailabilities,
  handleEdit,
  showExpressBooking,
  canDelete,
  canEditBookings,
  showEditBookings,
  isPast,
  showRequestedProfessionals,
  selectedItems,
  handlePress,
  disableActions,
}: IPharmacyWorkshiftListRow) =>
  data.map((item) => {
    const {
      id,
      bookingId,
      status,
      endDate,
      startDate,
      pharmacistName,
      type,
      workShiftId,
      totalHours,
      paidHourlyRate,
      pharmacyHourlyRate,
      professionalName,
      requestedCount = 0,
      externalPharmacistId,
      longTermShiftContractId,
      bookedProfessionals = [],
      totalShiftCount = 0,
      bookedShiftCount = 0,
      weekDayPaidHourlyRate = 0,
      weekendPaidHourlyRate = 0,
      pharmacyWeekDayHourlyRate = 0,
      replacementType,
      pharmacyWeekendHourlyRate = 0,
    } = item ?? {};

    const date = getDayMonthDateYear(startDate);
    const time = timeString(startDate, endDate, timeFormat);
    const postedCount = totalShiftCount - bookedShiftCount;
    const userId = item[idStore.id as keyof typeof item] as string;

    const dateTime = `${date} | ${time}`;

    const isBooked = type === PharmacyLegendVariant.booked
      || status === WorkshiftReportStatus.partiallyBooked;
    const isPosted = type === PharmacyLegendVariant.posted;
    const isRequested = type === PharmacyLegendVariant.requested || status === WorkshiftReportStatus.partiallyRequested;

    const name = isSelfService ? professionalName : pharmacistName;
    const isExternal = !!externalPharmacistId;
    const isLongBooking = type === PharmacyLegendVariant.booked && !!longTermShiftContractId;
    const isPartialBooked = status === WorkshiftReportStatus.partiallyBooked;

    const { color, backgroundColor } = ReplacementLabelsStyled?.[
        replacementType as keyof typeof ReplacementLabelsStyled
    ] ?? {};

    const normalizedBookedProfessionals = bookedProfessionals ?? [];
    const bookedProfessionalNameItems = normalizedBookedProfessionals.map(
      (inItem, index) =>
        ({
          key: `${inItem.professionalId}-${index}`,
          label: `${handleValue(inItem.professionalName)}`,
          onClick: goToUserProfile({
            personnelId: inItem.professionalId,
            userType: TypeOfUser.pharmacist,
            fromScreen: OtherScreens.ListOfWorkshifts,
          }),
        }),
    );

    const professionalHourlyRateItems = getBookedProfessionalRateItems({
      bookedProfessionals: normalizedBookedProfessionals,
      getRate: getProfessionalHourlyRate,
    });

    const pharmacyHourlyRateItems = getBookedProfessionalRateItems({
      bookedProfessionals: normalizedBookedProfessionals,
      getRate: getPharmacyHourlyRate,
    });

    const professionalHourlyRateConfig = professionalHourlyRateItems.length > 0
      ? {
        key: '8~',
        value: (
          <LimitedTooltipList
            items={professionalHourlyRateItems}
            isCenterAligned
            showAllItemsInTooltip
          />
        ),
        type: text,
        isCenterAligned: true,
      }
      : {
        ...rateRecord(
          {
            weekDayPaidHourlyRate,
            weekendPaidHourlyRate,
            rate: paidHourlyRate,
          },
          longTermShiftContractId,
        ),
        isCenterAligned: true,
      };

    const pharmacyHourlyRateConfig = pharmacyHourlyRateItems.length > 0
      ? {
        key: '11~',
        value: (
          <LimitedTooltipList
            items={pharmacyHourlyRateItems}
            isCenterAligned
            showAllItemsInTooltip
          />
        ),
        type: text,
        isCenterAligned: true,
      }
      : {
        ...rateRecord(
          {
            weekDayPaidHourlyRate: pharmacyWeekDayHourlyRate,
            weekendPaidHourlyRate: pharmacyWeekendHourlyRate,
            rate: pharmacyHourlyRate,
          },
          longTermShiftContractId,
        ),
        key: '11~',
        isCenterAligned: true,
      };

    const handleOnClick = () => {
      if (isBooked && (bookingId || longTermShiftContractId)) {
        showRequestCancellation({
          bookingId: bookingId ?? '',
          dateTime,
          longTermShiftContractId,
          professionalId: userId,
          bookedProfessionals: normalizedBookedProfessionals,
        });
      } else {
        showWorkshiftDelete(workShiftId, dateTime, longTermShiftContractId);
      }
    };

    const nameConfig = () => {
      if (isExternal) {
        return {
          key: '3',
          value: handleValue(professionalName ?? name ?? ''),
          type: text,
        };
      }
      if (isRequested) {
        return {
          key: '3',
          value: {
            label: requestedCount?.toString(),
            handlePress: () => {
              showRequestedProfessionals({
                workshiftId: workShiftId,
                longTermShiftContractId,
                professionalId: userId,
              });
            },
          },
          type: customLink,
        };
      }
      if (isBooked || isPartialBooked) {
        if (bookedProfessionalNameItems.length > 0) {
          return {
            key: '11',
            value: <LimitedTooltipList items={bookedProfessionalNameItems} showAllItemsInTooltip />,
            type: text,
          };
        }

        return {
          key: '11',
          value: {
            label: name,
            handlePress: goToUserProfile({
              personnelId: userId,
              userType: TypeOfUser.pharmacist,
              fromScreen: OtherScreens.ListOfWorkshifts,
            }),
          },
          type: customLink,
        };
      }

      return {
        key: '3',
        value: handleValue(professionalName ?? name ?? ''),
        type: text,
      };
    };

    const getEditDeleteConfig = () => {
      let deleteCancelConfig = {
        label: strings.delete,
        icon: deleteRequestIcon,
      };

      let editBookConfig = {
        label: strings.edit,
        icon: editWorkshift,
      };

      if (isRequested) {
        editBookConfig = {
          label: strings.expressBooking,
          icon: expressBookingIcon,
        };
      }

      if (isBooked) {
        deleteCancelConfig = {
          label: strings.cancel,
          icon: requestCancellationIcon,
        };
      }

      return { editBookConfig, deleteCancelConfig };
    };

    const { editBookConfig, deleteCancelConfig } = getEditDeleteConfig();

    return {
      key: id,
      data: [
        dateRecord(
          { startDate, endDate, timeFormat },
          PharmacistLegendVariant.workshift,
          longTermShiftContractId,
        ),
        {
          key: '1',
          value: {
            label: PharmacyWorkshiftStatusLabels()[status],
            style: statusStyle[status as keyof typeof statusStyle],
          },
          type: chip,
        },
        {
          key: '2',
          value: totalHours,
          type: text,
          isCenterAligned: true,
        },
        nameConfig(),
        {
          key: '122',
          value: {
            label:
              goToFallback(ReplacementLabels()[
                replacementType as keyof typeof ReplacementLabels
              ], fallbackText),
            style: {
              color,
              backgroundColor,
            },
          },
          type: chip,
        },
        professionalHourlyRateConfig,
        !isSelfService && pharmacyHourlyRateConfig,

        !isPast && {
          key: '7',
          value: [
            canEditBookings && !isExternal && !!longTermShiftContractId
              && normalizedBookedProfessionals.length > 0 && {
              key: 'editBookings',
              tooltip: strings.editBooking,
              icon: expressBookingIcon,
              disabled: disableActions,
              onClick: () =>
                showEditBookings(longTermShiftContractId, workShiftId, normalizedBookedProfessionals),
            },
            isPosted
              && !isExternal
              && !longTermShiftContractId && {
              key: '11',
              tooltip: strings.expressBooking,
              icon: expressBookingIcon,
              disabled: disableActions,
              onClick: () => {
                showExpressBooking(workShiftId);
              },
            },
            !(isExternal || isLongBooking) && {
              key: '9',
              tooltip: editBookConfig.label,
              icon: editBookConfig.icon,
              disabled: disableActions,
              onClick: handleEdit(
                longTermShiftContractId
                  ? statusTypeMap[status as keyof typeof statusTypeMap]
                  : type,
                bookingId,
                workShiftId,
              ),
            },
            !(isBooked || isExternal || longTermShiftContractId) && {
              key: '8',
              tooltip: strings.matchingAvailabilities,
              icon: findMatchingIcon,
              disabled: disableActions,
              onClick: () => {
                findMatchingAvailabilities(workShiftId);
              },
            },
            canDelete && {
              key: '10',
              tooltip: deleteCancelConfig.label,
              icon: deleteCancelConfig.icon,
              disabled: disableActions,
              onClick: handleOnClick,
            },
            canDelete
              && isPartialBooked && {
              key: '11',
              tooltip: strings.delete,
              icon: deleteRequestIcon,
              disabled: disableActions,
              onClick: () => {
                showWorkshiftDelete(
                  workShiftId,
                  dateTime,
                  longTermShiftContractId,
                );
              },
            },
          ],
          type: DynamicTableCell.bar,
        },
        !isPast
          && canDelete && {
          key: '8',
          value: {
            checked: !!selectedItems?.includes(workShiftId),
            handlePress: handlePress(workShiftId, postedCount),
            disabled: !isPosted,
            tooltipText: PharmacyWorkshiftStatusLabels()[status],
          },
          type: checkbox,
        },
      ].filter(Boolean),
    };
  });
